import { useSelector } from 'react-redux';
import { selectUserProfile } from '../pages/Layout/slice/selectors';
import { useMemo } from 'react';

export interface PermissionData {
  canEdit: boolean;
  canRead: boolean;
  admin: boolean;
  roles: string[];
}

export interface PermissionChecks {
  canEdit: boolean;
  canRead: boolean;
  canView: boolean;
  isAdmin: boolean;
  hasViewerRole: boolean;
  hasEditorRole: boolean;
  canAddItems: boolean;
  canEditItems: boolean;
  canDeleteItems: boolean;
  canManageContacts: boolean;
  canEditOverview: boolean;
  canEditDecisions: boolean;
  canEditAdditionalInfo: boolean;
  canEditMilestones: boolean;
  canEditGeneralInfo: boolean;
  // Additional permissions for specific features
  canCreateRSD: boolean;
  canCreateEvents: boolean;
  canCreateKeyDates: boolean;
  canEditEvents: boolean;
  canEditKeyDates: boolean;
  canDeleteEvents: boolean;
  canDeleteKeyDates: boolean;
}

/**
 * Common RBAC handler for managing access controls across the application
 *
 * KEY FEATURES:
 * - Admin status supersedes read/write flags - if user is admin, they can edit regardless of flags
 * - Centralized permission logic for consistency across components
 * - Event and Key Date specific permissions for admin-only access
 * - Multiple hooks for different use cases
 *
 * USAGE EXAMPLES:
 *
 * 1. For components that need full RBAC with permission data:
 *    const permissions = useRBAC(permissionData);
 *    if (permissions.canEditEvents) { ... }
 *
 * 2. For components that only need admin status:
 *    const isAdmin = useAdminCheck();
 *    if (isAdmin) { ... }
 *
 * 3. For Event/Key Date specific components:
 *    const { canCreateEvents, canEditEvents } = useEventKeyDatePermissions();
 *    if (canCreateEvents) { ... }
 *
 * @param permissionData - The permission data from the API response
 * @returns Object with permission checks for various actions
 */
export const useRBAC = (
  permissionData?: PermissionData | null
): PermissionChecks => {
  // Get user profile for admin status from existing Layout slice
  const userProfile = useSelector(selectUserProfile);
  const userIsAdmin = userProfile?.isAdmin || false;
  const userCanCreateRSD = userProfile?.canCreateRSD || false;

  const permissions = useMemo(() => {
    // If no permission data, default to read-only
    if (!permissionData) {
      return {
        canEdit: false,
        canRead: true,
        canView: true,
        isAdmin: userIsAdmin,
        hasViewerRole: false,
        hasEditorRole: false,
        canAddItems: userIsAdmin, // Only admins can add items
        canEditItems: userIsAdmin, // Only admins can edit items
        canDeleteItems: false,
        canManageContacts: false,
        canEditOverview: false,
        canEditDecisions: false,
        canEditAdditionalInfo: false,
        canEditMilestones: false,
        canEditGeneralInfo: false,
        // Event and Key Date specific permissions
        canCreateRSD: userCanCreateRSD,
        canCreateEvents: userIsAdmin,
        canCreateKeyDates: userIsAdmin,
        canEditEvents: userIsAdmin,
        canEditKeyDates: userIsAdmin,
        canDeleteEvents: userIsAdmin,
        canDeleteKeyDates: userIsAdmin
      };
    }

    const { canEdit, canRead, admin, roles = [] } = permissionData;

    // Basic permission checks
    const hasViewerRole = roles.includes('VIEWER');
    const hasEditorRole = roles.includes('EDITOR');
    // Use profile-based admin status instead of moments API admin flag
    const isAdmin = userIsAdmin;

    // If user is admin, they can do everything (supersedes all flags)
    if (isAdmin) {
      return {
        canEdit: true,
        canRead: true,
        canView: true,
        isAdmin: true,
        hasViewerRole,
        hasEditorRole,
        canAddItems: true,
        canEditItems: true,
        canDeleteItems: true,
        canManageContacts: true,
        canEditOverview: true,
        canEditDecisions: true,
        canEditAdditionalInfo: true,
        canEditMilestones: true,
        canEditGeneralInfo: true,
        canCreateRSD: userCanCreateRSD,
        // Event and Key Date specific permissions - admins have full access
        canCreateEvents: true,
        canCreateKeyDates: true,
        canEditEvents: true,
        canEditKeyDates: true,
        canDeleteEvents: true,
        canDeleteKeyDates: true
      };
    }

    // If canEdit is true, user can edit most things (but not admin-level actions)
    if (canEdit) {
      return {
        canEdit: true,
        canRead: true,
        canView: true,
        isAdmin: false,
        hasViewerRole,
        hasEditorRole,
        canAddItems: false, // Only admins can add items
        canEditItems: false, // Only admins can edit items
        canDeleteItems: false,
        canManageContacts: true,
        canEditOverview: true,
        canEditDecisions: true,
        canEditAdditionalInfo: true,
        canEditMilestones: true,
        canEditGeneralInfo: true,
        canCreateRSD: userCanCreateRSD,
        // Event and Key Date specific permissions - non-admins cannot access
        canCreateEvents: false,
        canCreateKeyDates: false,
        canEditEvents: false,
        canEditKeyDates: false,
        canDeleteEvents: false,
        canDeleteKeyDates: false
      };
    }

    // If only canRead is true, user can only view
    if (canRead) {
      return {
        canEdit: false,
        canRead: true,
        canView: true,
        isAdmin: false,
        hasViewerRole,
        hasEditorRole,
        canAddItems: false,
        canEditItems: false,
        canDeleteItems: false,
        canManageContacts: false,
        canEditOverview: false,
        canEditDecisions: false,
        canEditAdditionalInfo: false,
        canEditMilestones: false,
        canEditGeneralInfo: false,
        canCreateRSD: userCanCreateRSD,
        // Event and Key Date specific permissions - read-only users cannot access
        canCreateEvents: false,
        canCreateKeyDates: false,
        canEditEvents: false,
        canEditKeyDates: false,
        canDeleteEvents: false,
        canDeleteKeyDates: false
      };
    }

    // Default to no permissions if neither canEdit nor canRead
    return {
      canEdit: false,
      canRead: false,
      canView: false,
      isAdmin: false,
      hasViewerRole,
      hasEditorRole,
      canAddItems: false,
      canEditItems: false,
      canDeleteItems: false,
      canManageContacts: false,
      canEditOverview: false,
      canEditDecisions: false,
      canEditAdditionalInfo: false,
      canEditMilestones: false,
      canEditGeneralInfo: false,
      canCreateRSD: userCanCreateRSD,
      // Event and Key Date specific permissions - no access
      canCreateEvents: false,
      canCreateKeyDates: false,
      canEditEvents: false,
      canEditKeyDates: false,
      canDeleteEvents: false,
      canDeleteKeyDates: false
    };
  }, [permissionData, userIsAdmin, userCanCreateRSD]);

  return permissions;
};

/**
 * Helper function to extract permission data from API response
 * @param apiData - The full API response data
 * @returns Permission data object
 */
export const extractPermissionData = (apiData: any): PermissionData | null => {
  if (!apiData) {
    return null;
  }

  const extracted = {
    canEdit: apiData.canEdit === true,
    canRead: apiData.canRead === true,
    admin: apiData.admin === true,
    roles: Array.isArray(apiData.roles) ? apiData.roles : []
  };

  return extracted;
};

/**
 * Simple admin check hook for components that only need admin status
 * @returns boolean indicating if user is admin
 */
export const useAdminCheck = (): boolean => {
  const userProfile = useSelector(selectUserProfile);
  return userProfile?.isAdmin || false;
};

/**
 * Hook for Event and Key Date specific permissions
 * @returns Object with Event and Key Date specific permissions
 */
export const useEventKeyDatePermissions = () => {
  const userProfile = useSelector(selectUserProfile);
  const isAdmin = userProfile?.isAdmin || false;
  const canCreateRSD = userProfile?.canCreateRSD || false;

  return {
    canCreateRSD,
    canCreateEvents: isAdmin,
    canCreateKeyDates: isAdmin,
    canEditEvents: isAdmin,
    canEditKeyDates: isAdmin,
    canDeleteEvents: isAdmin,
    canDeleteKeyDates: isAdmin,
    isAdmin
  };
};
